import {
  getLayout,
  getGapBelowComponent,
  getGapAboveComponent,
  isMatchingSiblingGaps,
} from './utils/layout';

import { getSnapLineXPosition, getSnapLines } from './utils/snap';

import type { EditorAPI } from '#packages/editorAPI';
import type { LayoutY } from '#packages/layout';
import type { Gap } from './resizeGapsTypes';

export function createSnapHandler(editorAPI: EditorAPI, gap: Gap) {
  const { gapAboveInitLayout, gapBelowInitLayout } =
    getInitialLayoutsOfSiblingGaps(editorAPI, gap);

  const getSnapDataAndModifiedDeltaY = (deltaY: number) => {
    const initialHeight = gap.layout.height;
    const potentialHeight = initialHeight + deltaY;
    const gapAbove = gapAboveInitLayout;
    const gapBelow = gap.componentBelow
      ? getGapBelowForSnappingTo(editorAPI, gapBelowInitLayout, gap)
      : null;

    const { isMatchingGapAbove, isMatchingGapBelow } = isMatchingSiblingGaps(
      { y: gap.layout.y, height: potentialHeight },
      gapAboveInitLayout,
      gapBelow,
    );

    const snapData = [];
    const x = getSnapLineXPosition(gap);

    if (isMatchingGapAbove) {
      deltaY = gapAbove.height - initialHeight;
      snapData.push(...getSnapLines(x, gapAbove));
    }

    if (isMatchingGapBelow) {
      deltaY = gapBelow.height - initialHeight;
      snapData.push(...getSnapLines(x, gapBelow));
    }

    if (isMatchingGapAbove || isMatchingGapBelow) {
      snapData.push(
        ...getSnapLines(x, {
          y: gap.layout.y,
          height: initialHeight + deltaY,
        }),
      );
    }

    return {
      snapData,
      deltaY,
    };
  };

  return {
    getSnapDataAndModifiedDeltaY,
  };
}

function getInitialLayoutsOfSiblingGaps(editorAPI: EditorAPI, gap: Gap) {
  const gapAboveInitLayout = gap.componentAbove
    ? getGapAboveComponent(
        getLayout(editorAPI, gap.componentAbove),
        getLayout(editorAPI, gap.container),
        editorAPI.components
          .getSiblings(gap.componentAbove)
          .map((sibling) => getLayout(editorAPI, sibling)),
      )
    : null;

  const gapBelowInitLayout = gap.componentBelow
    ? getGapBelowComponent(
        getLayout(editorAPI, gap.componentBelow),
        getLayout(editorAPI, gap.container),
        editorAPI.components
          .getSiblings(gap.componentBelow)
          .map((sibling) => getLayout(editorAPI, sibling)),
      )
    : null;

  return { gapAboveInitLayout, gapBelowInitLayout };
}

/**
 * When resizing a gap, the gap below it is being
 * moved and may be resized as well. This function calculates
 * the position and height of the gap below when we
 * could snap our gap to it.
 */
function getGapBelowForSnappingTo(
  editorAPI: EditorAPI,
  gapBelowInitLayout: LayoutY,
  gap: Gap,
): LayoutY {
  const currentGapBelowLayout = getGapBelowComponent(
    getLayout(editorAPI, gap.componentBelow),
    getLayout(editorAPI, gap.container),
    editorAPI.components
      .getSiblings(gap.componentBelow)
      .map((sibling) => getLayout(editorAPI, sibling)),
  );

  if (currentGapBelowLayout.height === gapBelowInitLayout.height) {
    return currentGapBelowLayout;
  }

  const newHeight = (gap.layout.height + gapBelowInitLayout.height) / 2;
  const newY = gapBelowInitLayout.y + gapBelowInitLayout.height - newHeight;

  return {
    height: newHeight,
    y: newY,
  };
}
